import { useState, useEffect } from "react";
import skinSurveyIcon from "@/assets/icon-skin-survey.png";
import discountTagIcon from "@/assets/10-percent.png";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useLanguage } from "@/lib/i18n";
import { useCartStore } from "@/stores/cartStore";
import { useSurveyStore } from "@/stores/surveyStore";
import { fetchProductByHandle } from "@/lib/shopify";
import { enrichUserFromSurvey } from "@/lib/facebookPixel";
import { 
   
  Loader2, 
  ArrowLeft,
  ArrowRight, 
  CheckCircle2, 
  ShoppingBag,
  Gift,
  Clock,
  Star,
  Copy,
  Check,
  Minimize2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { trackSurveyEvent } from "@/lib/googleAnalytics";
interface SurveyQuestion {
  id: string;
  questionKey: string;
  options: { value: string; labelKey: string }[];
}

const surveyQuestions: SurveyQuestion[] = [
  {
    id: "skinType",
    questionKey: "survey.q1.question",
    options: [
      { value: "dry", labelKey: "survey.q1.dry" },
      { value: "oily", labelKey: "survey.q1.oily" },
      { value: "combination", labelKey: "survey.q1.combination" },
      { value: "normal", labelKey: "survey.q1.normal" },
    ],
  },
  {
    id: "mainConcern",
    questionKey: "survey.q2.question",
    options: [
      { value: "aging", labelKey: "survey.q2.aging" },
      { value: "acne", labelKey: "survey.q2.acne" },
      { value: "dullness", labelKey: "survey.q2.dullness" },
      { value: "hyperpigmentation", labelKey: "survey.q2.hyperpigmentation" },
    ],
  },
  {
    id: "sensitivity",
    questionKey: "survey.q3.question",
    options: [
      { value: "very_sensitive", labelKey: "survey.q3.very" },
      { value: "somewhat_sensitive", labelKey: "survey.q3.somewhat" },
      { value: "not_sensitive", labelKey: "survey.q3.not" },
    ],
  },
  {
    id: "age",
    questionKey: "survey.q5.question",
    options: [
      { value: "under_25", labelKey: "survey.q5.under25" },
      { value: "25_35", labelKey: "survey.q5.25to35" },
      { value: "35_45", labelKey: "survey.q5.35to45" },
      { value: "over_45", labelKey: "survey.q5.over45" },
    ],
  },
  {
    id: "routine",
    questionKey: "survey.q6.question",
    options: [
      { value: "minimal", labelKey: "survey.q6.minimal" },
      { value: "basic", labelKey: "survey.q6.basic" },
      { value: "moderate", labelKey: "survey.q6.moderate" },
      { value: "extensive", labelKey: "survey.q6.extensive" },
    ],
  },
  {
    id: "goals",
    questionKey: "survey.q8.question",
    options: [
      { value: "glow", labelKey: "survey.q8.glow" },
      { value: "anti_aging", labelKey: "survey.q8.antiAging" },
      { value: "hydration", labelKey: "survey.q8.hydration" },
      { value: "all", labelKey: "survey.q8.all" },
    ],
  },
];

interface RecommendedProduct {
  handle: string;
  name: string;
  quantity: number;
  reason: string;
  quantityReason?: string;
  priceNote?: string;
}

interface SavingsBreakdown {
  ifBoughtSeparately: string;
  yourPrice: string;
  youSave: string;
}

interface Recommendation {
  skinProfile: string;
  recommendationTier?: "single" | "combo" | "bundle";
  upgradeNote?: string;
  dealExplanation?: string;
  recommendedProducts: RecommendedProduct[];
  savingsBreakdown?: SavingsBreakdown;
  totalValue?: string;
  specialOffer?: string;
  urgencyMessage?: string;
  usageInstructions: {
    morning: string[];
    evening: string[];
  };
  expectedResults: {
    week2: string;
    week4: string;
    week8: string;
  };
  treatmentDuration: string;
  personalTips: string[];
}

export function GlobalSurvey() {
  const { t, language, currency } = useLanguage();
  const addItem = useCartStore(state => state.addItem);
  const openCart = useCartStore(state => state.openCart);
  
  // Survey store
  const isOpen = useSurveyStore(state => state.isOpen);
  const isMinimized = useSurveyStore(state => state.isMinimized);
  const isDocked = useSurveyStore(state => state.isDocked);
  const currentStep = useSurveyStore(state => state.currentStep);
  const answers = useSurveyStore(state => state.answers);
  const recommendation = useSurveyStore(state => state.recommendation);
  
  const closeSurvey = useSurveyStore(state => state.closeSurvey);
  const minimizeSurvey = useSurveyStore(state => state.minimizeSurvey);
  const expandFromBubble = useSurveyStore(state => state.expandFromBubble);
  const setCurrentStep = useSurveyStore(state => state.setCurrentStep);
  const setAnswer = useSurveyStore(state => state.setAnswer);
  const setRecommendation = useSurveyStore(state => state.setRecommendation);
  const resetSurvey = useSurveyStore(state => state.resetSurvey);
  const dockBubble = useSurveyStore(state => state.dockBubble);
  const undockBubble = useSurveyStore(state => state.undockBubble);
  
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponCopied, setCouponCopied] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  const loadingMessages = [
    t('survey.loading.analyzing'),
    t('survey.loading.planning'),
    t('survey.loading.matching'),
    t('survey.loading.finalizing'),
  ];

  // Rotate loading messages
  useEffect(() => {
    if (!isSubmitting) {
      setLoadingMessageIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isSubmitting, loadingMessages.length]);

  const DISCOUNT_CODE = "SURVEY10";

  // No auto-popup — survey only opens when user clicks the floating bubble

  const showResults = recommendation !== null;
  const currentQuestion = !showResults ? surveyQuestions[currentStep] : null;
  const progress = ((currentStep + 1) / surveyQuestions.length) * 100;
  const isLastStep = currentStep === surveyQuestions.length - 1;

  const handleAnswer = (value: string) => {
    if (!currentQuestion) return;
    setAnswer(currentQuestion.id, value);
    
    const stepNum = currentStep + 1;
    const percent = Math.round((stepNum / surveyQuestions.length) * 100);
    trackSurveyEvent('survey_progress', {
      step: stepNum,
      percent,
      question_id: currentQuestion.id,
    });

    setTimeout(() => {
      if (isLastStep) {
        submitSurvey();
      } else {
        setCurrentStep(currentStep + 1);
      }
    }, 300);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitSurvey = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/skin-survey`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            answers: { ...answers, language },
            currency,
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error(t("survey.errorTooMany"));
        }
        throw new Error(t("survey.errorGeneric"));
      }

      const data = await response.json();
      setRecommendation(data.recommendation);
      
      // Enrich tracking with female gender (skincare target audience)
      enrichUserFromSurvey({ gender: 'f' });
      
      const tier = data.recommendation?.recommendationTier || 'unknown';
      const productsCount = data.recommendation?.recommendedProducts?.length || 0;
      trackSurveyEvent('survey_complete', {
        recommendation_tier: tier,
        products_count: productsCount,
      });
    } catch (err) {
      console.error("Survey error:", err);
      setError(err instanceof Error ? err.message : t("survey.errorGeneric"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAllToCart = async () => {
    if (!recommendation?.recommendedProducts) return;
    
    setIsAddingToCart(true);
    
    try {
      for (const product of recommendation.recommendedProducts) {
        const productData = await fetchProductByHandle(product.handle);
        if (productData) {
          const variant = productData.variants.edges[0]?.node;
          if (variant) {
            await addItem({
              product: { node: productData },
              variantId: variant.id,
              variantTitle: variant.title,
              price: variant.price,
              quantity: product.quantity,
              selectedOptions: variant.selectedOptions || []
            });
          }
        }
      }
      
      const tier = recommendation.recommendationTier || 'unknown';
      const productsCount = recommendation.recommendedProducts?.length || 0;
      trackSurveyEvent('survey_add_to_cart', {
        recommendation_tier: tier,
        products_count: productsCount,
      });

      toast.success(t("survey.addedToCart"), {
        description: t("survey.personalizedRoutine"),
        position: "top-center",
      });
      
      closeSurvey();
      setTimeout(() => {
        openCart();
        resetSurvey();
      }, 300);
    } catch (err) {
      console.error("Error adding to cart:", err);
      toast.error(t("survey.errorAddingToCart"));
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleMinimize = () => {
    if (!recommendation) {
      trackSurveyEvent('survey_abandon', {
        last_step: currentStep + 1,
        percent_completed: Math.round(((currentStep) / surveyQuestions.length) * 100),
      });
    }
    minimizeSurvey();
  };

  const handleClose = () => {
    if (!recommendation) {
      trackSurveyEvent('survey_abandon', {
        last_step: currentStep + 1,
        percent_completed: Math.round(((currentStep) / surveyQuestions.length) * 100),
      });
    }
    closeSurvey();
  };

  const handleDialogChange = (open: boolean) => {
    if (!open && !isSubmitting) {
      // User closed the dialog - minimize instead of closing completely
      minimizeSurvey();
    }
  };

  // When submitting starts, close the survey dialog
  // When results arrive, reopen it
  const showSurveyDialog = isOpen && !isSubmitting;
  const showLoadingDialog = isSubmitting;

  // Check if survey has progress
  const hasProgress = Object.keys(answers).length > 0 || recommendation !== null;

  return (
    <>
      {/* Main Dialog */}
      <Dialog open={showSurveyDialog} onOpenChange={handleDialogChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader className="relative">
            <div className="flex items-center justify-between pe-8">
              <DialogTitle className="text-xl md:text-2xl font-bold flex items-center gap-2">
                <img src={skinSurveyIcon} alt="" className="w-6 h-6" />
                {showResults ? t("survey.resultsTitle") : t("survey.title")}
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={handleMinimize}
              >
                <Minimize2 className="w-4 h-4" />
              </Button>
            </div>
            <DialogDescription>
              {showResults ? t("survey.resultsSubtitle") : t("survey.subtitle")}
            </DialogDescription>
          </DialogHeader>

          {!showResults ? (
            <div className="py-4">
              {/* Progress bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                  <span>
                    {t("survey.question")} {currentStep + 1}/{surveyQuestions.length}
                  </span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              {/* Question */}
              {currentQuestion && (
                <div className="mb-6">
                  <h3 className="text-base md:text-lg font-medium mb-4">{t(currentQuestion.questionKey)}</h3>
                  <RadioGroup
                    value={answers[currentQuestion.id] || ""}
                    onValueChange={handleAnswer}
                    className="space-y-2 md:space-y-3"
                  >
                    {currentQuestion.options.map((option) => (
                      <div
                        key={option.value}
                        className={`flex items-center gap-3 p-3 md:p-4 rounded-lg border transition-all cursor-pointer ${
                          answers[currentQuestion.id] === option.value
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => handleAnswer(option.value)}
                      >
                        <RadioGroupItem value={option.value} id={`global-${option.value}`} />
                        <Label htmlFor={`global-${option.value}`} className="flex-1 cursor-pointer text-sm md:text-base">
                          {t(option.labelKey)}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3">
                {currentStep > 0 && (
                  <Button variant="outline" onClick={handleBack} className="flex-1">
                    {language === 'he' ? <ArrowRight className="w-4 h-4 me-2" /> : <ArrowLeft className="w-4 h-4 me-2" />}
                    {t("survey.back")}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            /* Results view */
            <div className="py-4 space-y-5">
              {/* Savings Banner — only for bundle tier */}
              {recommendation.savingsBreakdown && recommendation.recommendationTier === "bundle" && (
                <div className={`p-4 ${language === 'he' ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-emerald-500 to-green-500 rounded-xl text-white`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Gift className="w-5 h-5" />
                    <span className="font-bold text-lg">{t('survey.bestDeal')}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center mt-3">
                    <div className="bg-white/20 rounded-lg p-2">
                      <p className="text-xs opacity-80">{t('survey.separatePrice')}</p>
                      <p className="font-bold line-through">{recommendation.savingsBreakdown.ifBoughtSeparately}</p>
                    </div>
                    <div className="bg-white/30 rounded-lg p-2">
                      <p className="text-xs opacity-80">{t('survey.yourPrice')}</p>
                      <p className="font-bold text-lg">{recommendation.savingsBreakdown.yourPrice}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-2">
                      <p className="text-xs opacity-80">{t('survey.youSave')}</p>
                      <p className="font-bold text-yellow-200">{recommendation.savingsBreakdown.youSave}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Deal Explanation */}
              {recommendation.dealExplanation && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-800">{recommendation.dealExplanation}</p>
                </div>
              )}

              {/* Skin Profile */}
              <div className="p-4 bg-secondary/30 rounded-xl">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  {t("survey.yourProfile")}
                </h4>
                <p className="text-sm text-muted-foreground">{recommendation.skinProfile}</p>
              </div>

              {/* Recommended Products */}
              <div className="space-y-3">
                <h4 className="font-semibold">{t("survey.yourPersonalizedRoutine")}</h4>
                {recommendation.recommendedProducts.map((product: RecommendedProduct, idx: number) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-white/80 rounded-xl border border-primary/20 flex items-start gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-primary">{product.quantity}x</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{product.name}</p>
                        {product.priceNote && (
                          <span className="text-xs text-emerald-600 font-medium">{product.priceNote}</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{product.reason}</p>
                      {product.quantity > 1 && product.quantityReason && (
                        <p className="text-xs text-primary/70 mt-1.5 flex items-center gap-1">
                          <span className="inline-block w-3.5 h-3.5">📦</span>
                          {product.quantityReason}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Upgrade Section — only for single/combo tiers */}
              {recommendation.upgradeNote && recommendation.recommendationTier !== "bundle" && (
                <div className={`p-4 ${language === 'he' ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-primary/5 to-primary/10 rounded-xl border border-primary/20`}>
                  <p className="font-semibold text-sm mb-2 text-primary">{t('survey.upgradeOption')}</p>
                  <p className="text-xs text-muted-foreground mb-3">{recommendation.upgradeNote}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-primary/30 text-primary hover:bg-primary/5"
                    onClick={() => {
                      closeSurvey();
                      window.location.href = '/product/complete-pdrn-trio-bundle';
                    }}
                  >
                    {t('survey.upgradeToBundle')}
                  </Button>
                </div>
              )}

              {/* Treatment Duration */}
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm text-amber-800">{t("survey.treatmentPlan")}</p>
                  <p className="text-xs text-amber-600">{recommendation.treatmentDuration}</p>
                </div>
              </div>

              {/* Expected Results Timeline */}
              <div>
                <h4 className="font-semibold mb-3 text-sm">{t("survey.expectedResults")}</h4>
                <div className="space-y-2">
                  <div className="flex gap-3 items-start">
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded flex-shrink-0">
                      2 {t("survey.weeks")}
                    </span>
                    <span className="text-xs text-muted-foreground">{recommendation.expectedResults.week2}</span>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded flex-shrink-0">
                      4 {t("survey.weeks")}
                    </span>
                    <span className="text-xs text-muted-foreground">{recommendation.expectedResults.week4}</span>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="px-2 py-1 bg-primary/30 text-primary text-xs font-medium rounded flex-shrink-0">
                      8 {t("survey.weeks")}
                    </span>
                    <span className="text-xs text-muted-foreground">{recommendation.expectedResults.week8}</span>
                  </div>
                </div>
              </div>

              {/* Exclusive Discount Code */}
              <div className={`p-4 ${language === 'he' ? 'bg-gradient-to-l' : 'bg-gradient-to-r'} from-emerald-50 to-green-50 rounded-xl border border-emerald-200`}>
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-5 h-5 text-emerald-600" />
                  <span className="font-bold text-emerald-800">{t("survey.exclusiveDiscount")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-white rounded-lg border-2 border-dashed border-emerald-300 px-4 py-2">
                    <span className="font-mono font-bold text-lg text-emerald-700">{DISCOUNT_CODE}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => {
                      navigator.clipboard.writeText(DISCOUNT_CODE);
                      setCouponCopied(true);
                      toast.success(t("survey.couponCopied"));
                      setTimeout(() => setCouponCopied(false), 2000);
                    }}
                  >
                    {couponCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-emerald-600 mt-2">{t("survey.useAtCheckout")}</p>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3 pt-2">
                <Button
                  onClick={handleAddAllToCart}
                  disabled={isAddingToCart}
                  className="w-full bg-gradient-button hover:opacity-90 text-white py-6 text-base font-bold shadow-lg"
                >
                  {isAddingToCart ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5 me-2" />
                      {recommendation.recommendationTier === "single"
                        ? t("survey.addSingleToCart")
                        : t("survey.addRoutineToCart")}
                    </>
                  )}
                </Button>
                
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span>{t("survey.satisfactionGuarantee")}</span>
                </div>

                <Button 
                  variant="ghost" 
                  onClick={handleClose} 
                  className="w-full text-muted-foreground hover:text-foreground"
                >
                  {t("survey.maybeLater")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Loading Dialog — separate from survey */}
      <Dialog open={showLoadingDialog}>
        <DialogContent className="sm:max-w-sm" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="sr-only">{t('survey.loading.analyzing')}</DialogTitle>
            <DialogDescription className="sr-only">{t('survey.loading.analyzing')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-10 gap-5">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm font-medium text-foreground text-center transition-opacity duration-500">
              {loadingMessages[loadingMessageIndex]}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Docked Tab — narrow strip on screen edge */}
      {isMinimized && isDocked && (
        <button
          onClick={undockBubble}
          className="fixed bottom-24 end-0 z-50 ltr:translate-x-[30%] rtl:-translate-x-[30%] opacity-60 ltr:hover:translate-x-[10%] rtl:hover:-translate-x-[10%] hover:opacity-100 transition-all duration-300"
          aria-label={t("survey.continueSurvey")}
        >
          <img src={discountTagIcon} alt="10% off" className="w-14 h-auto" />
        </button>
      )}

      {/* Sticky Bubble — full size */}
      {isMinimized && !isDocked && (
        <div className="fixed bottom-6 end-6 z-50 flex flex-col items-end gap-2">
          {/* Tooltip */}
          <div className="bg-white rounded-xl shadow-lg shadow-primary/15 border border-border/50 px-3 py-2 max-w-[200px] text-center animate-fade-in">
            <p className="text-xs font-medium text-foreground leading-tight">{t('survey.bubbleTooltip')}</p>
          </div>
          <div className="relative">
            {/* X button to dock */}
            <button
              onClick={dockBubble}
              className="absolute -top-1 -start-1 z-10 w-5 h-5 rounded-full bg-foreground/70 text-white flex items-center justify-center hover:bg-foreground transition-colors"
              aria-label="Hide"
            >
              <X className="w-3 h-3" />
            </button>
            <button
              onClick={() => {
                if (Object.keys(answers).length === 0 && !recommendation) {
                  trackSurveyEvent('survey_start', { language, currency });
                }
                expandFromBubble();
              }}
              className="w-14 h-14 rounded-full bg-white shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-110 transition-transform border border-primary/20 bubble-attention"
              aria-label={t("survey.continueSurvey")}
            >
              <img src={skinSurveyIcon} alt="Skin Survey" className="w-8 h-8" />
              {hasProgress && (
                <span className="absolute -top-1 -end-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                  {Object.keys(answers).length}
                </span>
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
